import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageLightbox } from '../../components/image-lightbox.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import {
  type ChainLastRun,
  type ChainLink,
  type ChainRelease,
  parseReleaseConflict,
  runsApi,
} from '../../lib/runs-api.js';

// =================================================================================================
// Task 8 (#113, A1/A2/A5): DIE KETTENLEISTE — „wer war vor mir dran, was ist dabei entstanden".
//
// Sie haengt in Task 9 ueber dem Ausfuehrungsfeld. Vier Entscheidungen tragen sie:
//
// 1. ZWEI FLACHE ZEILEN — Kopf oben, Glieder darunter, GENAU WIE DIE A1-SKIZZE.
//    Diese Entscheidung wurde einmal umgedreht; sie bitte nicht ein zweites Mal zurueckdrehen.
//    Der scheinbare Konflikt: die Skizze zeigt zwei Zeilen, das Akzeptanzkriterium sagt
//    „zugeklappt hoechstens eine Zeile". Aufgeloest wird er ueber die Platzrechnung auf dem
//    Zielschirm 1280 px: verfuegbar sind rund 850 px (Gesamtbreite minus Playlist `w-[360px]`
//    minus Panel-Innenabstand). Ein Kopf von 240 px plus vier Glieder zu je 185-250 px sind
//    ~1012 px — bereits bei vier Gliedern passt die einzeilige Fassung nicht, bei fuenf waere
//    rund ein Drittel der Kette nur durch Scrollen erreichbar. A1 verlangt Nummer, Kurztitel,
//    Zustand und Tester je Glied als SICHT, nicht als Scrollziel. „Eine Zeile" ist deshalb als
//    „die Glieder brechen nicht um" zu lesen, nicht als „die ganze Leiste ist eine Textzeile".
//    Zweizeilig bleiben ~850 px fuer die Glieder allein: fuenf Glieder brauchen ~925-1250 px,
//    laufen also weiterhin ueber — deshalb der Ueberlauf-Hinweis (#78, keine stille Kappung)
//    AUSSERHALB des Scroll-Containers.
//    Die Gliederzeile bricht nicht um (`flex-nowrap`) und scrollt waagerecht
//    (`overflow-x-auto`); dazu `overflow-y-hidden`, weil `overflow-x` allein die Y-Achse nach
//    CSS-Spec von `visible` auf `auto` umrechnet — der waagerechte Balken laege dann INNERHALB
//    der Box und klemmte die Chips ab. Eine feste Hoehe (`h-8`) traegt die Einzeiligkeit nicht
//    und wurde genau deswegen entfernt.
//    Kopfzeile und „hakt bei dir" stehen NICHT in der scrollenden Zeile: A1 nennt beide als
//    immer sichtbar, in der Scrollzeile wandern sie als Erstes aus dem Bild (`ml-auto` wirkt
//    zudem nur, solange freier Platz da ist).
//
// 2. INLINE, KEIN PORTAL. Die Aufklappung steht unter der Zeile und SCHIEBT das Ausfuehrungsfeld
//    nach unten, statt es zu verdecken. Die Architektur schliesst ein Portal/Overlay ausdruecklich
//    aus — die einzige Ausnahme ist die vorhandene `ImageLightbox` fuer die Bildvergroesserung,
//    die hier bewusst WIEDERVERWENDET und nicht nachgebaut wird.
//
// 3. AUFKLAPPZUSTAND LEBT IN DER KOMPONENTE, nicht in sessionStorage (architektur.md, „Aufklapp-
//    zustand"): Er ist an den gerade bearbeiteten Testfall gebunden und beim naechsten Fall
//    bedeutungslos — anders als der Playlist-Gruppen-Klappzustand, der bewusst gemerkt wird.
//    Der Default (direkter Vorgaenger offen) wird beim Rendern ABGELEITET; gespeichert werden nur
//    die ausdruecklichen Klicks des Nutzers. Deshalb braucht es keinen Effekt, der State aus
//    nachgeladenen Query-Daten nachzieht — der Default steht sofort, sobald die Daten da sind.
//
// 4. ANHAENGE ERST BEIM AUFKLAPPEN. Ebenfalls Akzeptanzkriterium, keine Sparsamkeit aus
//    Geschmack: Anhaenge liegen als data-URL mit bis zu 2 MB je Stueck und ohne Mengenlimit im
//    jsonb. Durchgesetzt wird das nicht durch eine Bedingung IM Abruf, sondern dadurch, dass die
//    abrufende Komponente (`ChainLinkDetail`) erst beim Aufklappen ueberhaupt montiert wird.
// =================================================================================================

// Zustandszeichen je Glied. Bewusst Zeichen statt `ResultChip`: die Chips tragen ein Wort und
// sprengen bei vier Gliedern die eine Zeile. Der Wortlaut geht nicht verloren — er steht als
// `sr-only`-Text daneben und damit im zugaenglichen Namen des Umschalters.
const RESULT_GLYPH: Record<string, string> = {
  successful: '✓',
  error: '✕',
  blocked: '⏸',
  repeat: '↻',
  open: '○',
};

// #190: abgelehnter Fehlerlauf — der Testfall gilt dann als offen (Playlist-Karte „Defect …
// abgelehnt"). Die Entscheidung trifft allein der Server (`rejected`, services/rejected-runs.ts):
// alle Defects des Laufs abgelehnt UND am Testfall kein anderer Defect mehr offen. Aus
// `defect.status` allein abgeleitet, zeigte die Leiste im Dubletten-Fall (D2 abgelehnt, D1 noch in
// Behebung) „abgelehnt", während die Playlist „Fehler" zeigt.
export function isRejectedErrorRun(run: ChainLastRun | null): boolean {
  // `result === 'error'` zusätzlich abgesichert: ein bestandener Lauf bleibt ✓, auch wenn ein
  // fehlerhafter Client/Server `rejected` setzt.
  return run?.result === 'error' && run.rejected === true;
}

// #190: der Defect, den die Leiste/Gruppe als „abgelehnt" nennt — null, wenn der Lauf kein
// abgelehnter Fehlerlauf ist oder keinen Defect trägt. Einzige Stelle für diese Zweigbedingung.
export function rejectedDefectOf(run: ChainLastRun | null): ChainLastRun['defect'] {
  return run && isRejectedErrorRun(run) ? run.defect : null;
}

function glyphOf(l: ChainLink): string {
  // #190: Lauf bleibt der echte (Lauf-Sicht), aber ein abgelehnter Fehler ist kein ✕ mehr — die
  // Kette steht trotzdem (blocked_by unverändert).
  if (isRejectedErrorRun(l.last_run)) return '○';
  if (l.last_run) return RESULT_GLYPH[l.last_run.result] ?? '○';
  return '○';
}

// Verbindet Umschalter (`aria-controls`) und Aufklappung (`id`). Beide Stellen muessen dieselbe
// ID bilden — deshalb eine Funktion statt zweier Schablonen-Literale.
const detailId = (testCaseId: string) => `chain-link-detail-${testCaseId}`;

export function ChainBar({
  projectId,
  planId,
  e2eId,
  testCaseId,
  canRelease,
}: {
  projectId: string;
  planId: string;
  e2eId: string;
  /** Das eigene Glied: wird hervorgehoben und bestimmt, wer der direkte Vorgaenger ist. */
  testCaseId: string;
  /**
   * A5: Darf dieser Nutzer ein gesperrtes Glied freigeben (`e2e.release`)? Die Leiste kennt selbst
   * KEINE Rollen — der Aufrufer rechnet es einmal aus (`useScopedUser` + `canInAnyScope`, Muster
   * aus my-tests-page.tsx) und reicht das Ergebnis durch. Dieselbe Trennung wie bei
   * `canViewDefect` an der Playlist.
   */
  canRelease: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  const chainQuery = useQuery({
    queryKey: ['e2e-chain', projectId, planId, e2eId],
    queryFn: () => runsApi.chain(projectId, planId, e2eId),
    enabled: Boolean(projectId) && Boolean(planId) && Boolean(e2eId),
  });

  // Nur die AUSDRUECKLICHEN Klicks. Der Default (direkter Vorgaenger offen) wird unten abgeleitet.
  // Der `key` bindet die Klicks an das Paar (Kette, Testfall): wechselt die Bearbeitung, sind sie
  // gegenstandslos — genau die Begruendung, aus der der Zustand auch nicht in sessionStorage
  // gehoert. Ohne Effekt, damit der Wechsel keinen zweiten Render-Durchgang kostet.
  const openKey = `${e2eId}|${testCaseId}`;
  const [opened, setOpened] = useState<{ key: string; by: Record<string, boolean> }>({
    key: openKey,
    by: {},
  });
  const overrides = opened.key === openKey ? opened.by : {};
  const toggle = (id: string, isOpen: boolean) =>
    setOpened({ key: openKey, by: { ...overrides, [id]: !isOpen } });

  // A5: eine gerade angelegte (oder per 409 zurueckgemeldete) Freigabe, bevor die Kettensicht neu
  // geladen ist. Der Server ist die Wahrheit; das hier ueberbrueckt nur die Zeit bis zum Refetch.
  // Derselbe `key`-Riegel wie oben: A6 laesst DENSELBEN Testfall in zwei Ketten zu, eine optimistisch
  // gemerkte Freigabe darf beim Wechsel des Paares nicht mitwandern. Heute haengt `ExecutionPanel`
  // die Leiste per `key={selectedKey}` neu auf und montiert sie dabei ohnehin neu — der Riegel ist
  // die Absicherung gegen den Tag, an dem dieser `key` verschwindet. Entweder beide Zustaende sind
  // gesichert oder keiner; halb gesichert ist die Falle.
  const [fresh, setFresh] = useState<{ key: string; by: Record<string, ChainRelease> }>({
    key: openKey,
    by: {},
  });
  const freshReleases = fresh.key === openKey ? fresh.by : {};
  const rememberRelease = (id: string, release: ChainRelease) =>
    setFresh({ key: openKey, by: { ...freshReleases, [id]: release } });

  // #78 („keine stille Kappung"): Laeuft die Gliederzeile ueber, sagt es ein Hinweis AUSSERHALB
  // des Scroll-Containers — im Container scrollte er mit weg. Gemessen statt geraten: eine feste
  // Glied-Zahl als Schwelle traefe weder den schmalen Schirm noch die kurzen Titel.
  const rowRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const linkCount = chainQuery.data?.links.length ?? 0;
  useEffect(() => {
    const measure = () => {
      const el = rowRef.current;
      setOverflowing(linkCount > 0 && el !== null && el.scrollWidth > el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [linkCount]);

  // #89: „Ein Query-Fehler darf NIE als Leerzustand erscheinen." Frueher fielen „Abruf
  // fehlgeschlagen", „laedt noch" und „Kette leer" in denselben `return null` — die Leiste
  // verschwand rueckstandslos und der Tester fuehrte seinen Kettenschritt OHNE die Belegnummern
  // der Vorgaenger aus, also genau ohne das, wofuer dieses Feature gebaut wurde. Die einzeilige
  // `compact`-Variante blockiert das Ausfuehrungsfeld nicht (Muster: execution-panel.tsx,
  // signoff-page.tsx).
  if (chainQuery.isError) {
    return (
      <section
        aria-label={t('exec.chain.label')}
        className="rounded-card border border-[var(--border)] bg-bg-subtle px-2 py-1"
      >
        <QueryErrorNotice compact onRetry={() => void chainQuery.refetch()} />
      </section>
    );
  }

  const view = chainQuery.data;
  // Ab hier nur noch die beiden lautlosen Faelle: waehrend des Ladens gibt es nichts zu zeigen,
  // und eine LEERE Kette ist kein Fehler, sondern eine Auskunft ohne Inhalt.
  if (!view || view.links.length === 0) return null;

  // Serverseitig bereits sortiert; die Kopie hier ist der Riegel gegen eine spaetere Aenderung
  // dort. Die Auswertung arbeitet auf der SORTIERTEN LISTE, nicht auf `sequence ± 1` — Luecken
  // durch abgewaehlte Glieder bleiben so folgenlos (architektur.md, „Offene technische
  // Entscheidung").
  const links = [...view.links].sort((a, b) => a.sequence - b.sequence);
  const ownIndex = links.findIndex((l) => l.test_case_id === testCaseId);
  // A2: „Der direkte Vorgaenger ist beim Oeffnen automatisch aufgeklappt, alle frueheren
  // zugeklappt." Er ist der NACHBAR in der Liste, nicht „Sequenz minus eins".
  const predecessorId = ownIndex > 0 ? links[ownIndex - 1]?.test_case_id : undefined;

  // A1: „Der Kettenzustand ‚liegt gerade bei dir' ist erkennbar." Das offene Glied ist das erste,
  // das noch nicht bestanden hat — steht die eigene Position dort, hakt es bei dir. Bewusst
  // `result !== 'successful'` (nicht `state`): `repeat`, `error` und `blocked` halten die Kette
  // genauso auf wie ein fehlender Lauf (A4).
  const firstOpen = links.find((l) => l.last_run?.result !== 'successful');
  const stalledOnYou = Boolean(firstOpen) && firstOpen?.test_case_id === testCaseId;

  // Review-Befund Minor 2 (Task 10): `e2e_processes.name_en` ist `notNull().default('')`
  // (schema.ts:296) — eine unuebersetzte Kette liefert '' und nicht null. Ohne den Rueckfall stuende
  // in der englischen Oberflaeche eine Kopfzeile ohne Kettennamen, obwohl ein deutscher vorliegt.
  const chainName = pickLang(lang, view.name_de, view.name_en);
  // Der angezeigte Text kuerzt ab (`truncate`) — der `title` traegt deshalb das GANZE, was
  // dasteht (Stream, Kettennummer, Kettenname), nicht nur den Kettennamen.
  const headText = `${view.stream ? `${view.stream.name} · ` : ''}${view.human_id} · ${chainName}`;

  return (
    <section
      aria-label={t('exec.chain.label')}
      className="flex flex-col rounded-card border border-[var(--border)] bg-bg-subtle"
    >
      {/* Zeile 1: Kopf. Eigener, NICHT scrollender Block (s. Kopfkommentar 1) — Kettenbezeichnung
          links, „hakt bei dir" rechts, beide immer sichtbar. */}
      <div className="flex items-center gap-2 px-2 pt-1 text-[11px]">
        <span className="min-w-0 flex-1 truncate font-medium text-text-body" title={headText}>
          {view.stream ? `${view.stream.name} · ` : ''}
          <span className="font-mono">{view.human_id}</span>
          {' · '}
          {chainName}
        </span>
        {stalledOnYou && (
          <span className="shrink-0 rounded-chip bg-[var(--tint-blue)] px-2 py-0.5 font-medium text-text-body">
            {t('exec.chain.stalledOnYou')}
          </span>
        )}
      </div>
      {/* Zeile 2: die Glieder. `flex-nowrap` verbietet den Umbruch, `overflow-x-auto` faengt lange
          Ketten waagerecht ab, `overflow-y-hidden` haelt den waagerechten Balken davon ab, die
          Chips abzuklemmen. Keine feste Hoehe. */}
      <div
        ref={rowRef}
        data-testid="chain-bar-row"
        className="flex flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden px-2 pt-1 pb-1 text-[11px]"
      >
        {links.map((l) => {
          const isOwn = l.test_case_id === testCaseId;
          const rejectedDefect = rejectedDefectOf(l.last_run);
          const isOpen = overrides[l.test_case_id] ?? l.test_case_id === predecessorId;
          const title = pickLang(lang, l.title_de, l.title_en);
          const tester = isOwn
            ? t('exec.chain.you')
            : (l.assigned_tester_name ?? l.last_run?.tester_name ?? t('exec.chain.unknownTester'));
          return (
            <button
              key={l.test_case_id}
              type="button"
              aria-expanded={isOpen}
              // Die Aufklappungen stehen in einer ZWEITEN Schleife, im DOM also hinter allen
              // Umschaltern — ohne diese Verbindung fuehrt `aria-expanded` Vorlesegeraete
              // nirgendwohin. Nur wenn der Bereich wirklich existiert: eine ins Leere zeigende
              // `aria-controls`-ID ist selbst ein Fehler.
              aria-controls={isOpen ? detailId(l.test_case_id) : undefined}
              onClick={() => toggle(l.test_case_id, isOpen)}
              // A1: „die eigene Position ist hervorgehoben".
              className={`flex shrink-0 items-center gap-1 rounded-chip border px-2 py-0.5 ${
                isOwn
                  ? 'border-brand-accent bg-brand-accent/10 font-semibold text-text-primary'
                  : 'border-[var(--border)] text-text-body'
              } ${l.state === 'waiting' ? 'opacity-60' : ''}`}
            >
              <span className="font-mono">{l.sequence}</span>
              <span className="max-w-[140px] truncate">{title}</span>
              <span aria-hidden="true">{glyphOf(l)}</span>
              {/* Das Zeichen allein hat keinen Namen — der Wortlaut steht hier fuer Vorlesegeraete
                  und faerbt zugleich den zugaenglichen Namen des Umschalters ein. */}
              <span className="sr-only">
                {rejectedDefect
                  ? t('exec.rejectedDefect', { id: rejectedDefect.human_id })
                  : l.last_run
                    ? t(`results.${l.last_run.result}`)
                    : t('exec.chain.notRunYet')}
              </span>
              {/* Breitenriegel wie am Titel: die Paar-Zellen aus dem Import
                  („M. Walkenhorst / A. Wenckebach", anforderung.md „Risiken") zoegen ein Glied
                  allein ueber 250 px und schoeben alles Folgende in den Scrollbereich. */}
              <span className="max-w-[110px] truncate text-text-muted" title={tester}>
                {tester}
              </span>
            </button>
          );
        })}
      </div>
      {/* #78: keine stille Kappung. Steht bewusst UNTER dem Scroll-Container, nicht darin. Ohne
          Zahl: wie viele Glieder rechts liegen, wuesste erst eine Messung je Chip — der Hinweis
          soll sagen, DASS dort noch etwas ist. */}
      {overflowing && (
        <p className="px-2 pb-1 text-[10px] text-text-muted">{t('exec.chain.overflowHint')}</p>
      )}
      {links.map((l) => {
        const isOpen = overrides[l.test_case_id] ?? l.test_case_id === predecessorId;
        if (!isOpen) return null;
        return (
          <ChainLinkDetail
            // `key` auf dem Glied: die Detailansicht haelt eigenen Zustand (Lightbox-Index,
            // Freigabe-Formular) — ohne ihn wanderte er beim Wechsel des offenen Glieds mit.
            key={l.test_case_id}
            id={detailId(l.test_case_id)}
            projectId={projectId}
            planId={planId}
            e2eId={e2eId}
            link={l}
            lang={lang}
            canRelease={canRelease}
            release={freshReleases[l.test_case_id] ?? l.release}
            onReleased={(r) => rememberRelease(l.test_case_id, r)}
          />
        );
      })}
    </section>
  );
}

// A2: Was ein Vorgaenger hinterlassen hat — Tester, Zeitpunkt, Ergebnistext, Anhaenge. Eigene
// Komponente, weil DARIN der Anhang-Abruf haengt: Sie wird erst beim Aufklappen montiert, und
// damit laeuft der Abruf erst dann. Eine Bedingung im Abruf (`enabled: isOpen`) haette dasselbe
// getan, aber jedes zugeklappte Glied haette trotzdem seinen Query-Platz belegt.
function ChainLinkDetail({
  id,
  projectId,
  planId,
  e2eId,
  link,
  lang,
  canRelease,
  release,
  onReleased,
}: {
  /** Ziel des `aria-controls` am zugehoerigen Umschalter. */
  id: string;
  projectId: string;
  planId: string;
  e2eId: string;
  link: ChainLink;
  lang: string;
  canRelease: boolean;
  release: ChainRelease | null;
  onReleased: (release: ChainRelease) => void;
}) {
  const { t } = useTranslation();
  const run = link.last_run;
  const rejectedDefect = rejectedDefectOf(run);
  // Whole-Branch-Review, Important 2: die ANZEIGE-Frage, dasselbe Gegenstueck wie `isLocked` in der
  // Playlist. `blocked_by` traegt seit der Entkopplung das reine Vorgaenger-Urteil und steht damit
  // auch an einem BESTANDENEN Glied, dessen Vorgaenger nachtraeglich wieder rot geworden ist.
  // Ungefiltert stuende an einem gruenen Glied „wartet auf Schulz — Schritt 2" und darunter das
  // Freigabe-Formular (A5) — ein Notausgang fuer eine Tuer, durch die der Tester laengst hindurch
  // ist. Ob ein WIEDERHOLUNGSLAUF durchgeht, entscheidet weiterhin `blocked_by` selbst (dort, wo
  // der Lauf abgeschickt wird: execution-panel.tsx).
  const waitingFor = run?.result === 'successful' ? null : link.blocked_by;
  const attachmentsQuery = useQuery({
    queryKey: ['run-attachments', projectId, planId, run?.run_id],
    queryFn: () => runsApi.attachments(projectId, planId, run?.run_id ?? ''),
    // Ein Lauf ohne Anhaenge braucht den Endpunkt gar nicht erst — die Zahl steht bereits in der
    // Kettensicht.
    enabled: Boolean(run?.run_id) && (run?.attachment_count ?? 0) > 0,
    // Die Anhaenge EINES Laufs aendern sich nie: ein erneuter Lauf bekommt eine neue `run_id` und
    // damit einen neuen Query-Schluessel. Ohne `staleTime` zoege jedes erneute Aufklappen dieselben
    // Megabyte noch einmal ueber die Leitung — genau die Last, die dieser Schnitt vermeiden soll.
    staleTime: Number.POSITIVE_INFINITY,
  });
  const images = attachmentsQuery.data?.screenshots ?? [];
  const [zoom, setZoom] = useState<number | null>(null);
  // Stabil halten: der Effekt in ImageLightbox haengt an onClose und ruft dort focus() (#37) —
  // ein Inline-Arrow risse den Fokus bei jedem Render erneut ins Overlay.
  const closeZoom = useCallback(() => setZoom(null), []);

  const imageLabel = (i: number) => t('exec.chain.attachmentAlt', { n: i + 1, id: link.human_id });

  return (
    <div id={id} className="border-t border-[var(--border)] px-3 py-2 text-xs text-text-body">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-muted">
        <span className="font-mono">{link.human_id}</span>
        {run && (
          <>
            {/* Der Lauf ueberlebt den Nutzer (`actual_tester_id` ist set null) — der Name kann
                fehlen. */}
            <span>{run.tester_name ?? t('exec.chain.unknownTester')}</span>
            <span>{new Date(run.executed_at).toLocaleString('de-DE')}</span>
            {/* #126/#186: „Testfall bestanden, Fehler gemeldet" — der Lauf gilt als bestanden (Kette
                läuft weiter), aber der Tester hat einen Defect gemeldet. Nur die Nummer als Text,
                kein Link: Tester haben keine /defects-Route. Beim Fehler-Lauf bleibt der Hinweis
                weg — dort ist der Defect das erwartete Ergebnis, das Glied zeigt ohnehin ✕. #190:
                meldet der Server den Lauf als abgelehnten Fehlerlauf (`rejected`), steht das Glied
                neutral (○) und die Detailzeile nennt den Defect. */}
            {run.result === 'successful' && run.defect && (
              <span className="font-medium text-text-body">
                {t('exec.chain.passedWithDefect', { id: run.defect.human_id })}
              </span>
            )}
            {rejectedDefect && (
              <span className="font-medium text-text-body">
                {t('exec.rejectedDefect', { id: rejectedDefect.human_id })}
              </span>
            )}
          </>
        )}
      </div>
      {run?.result_description ? (
        <p className="mt-1 whitespace-pre-wrap">{run.result_description}</p>
      ) : (
        run && <p className="mt-1 text-text-muted">{t('exec.chain.noResultText')}</p>
      )}
      {!run && !release && !waitingFor && (
        <p className="mt-1 text-text-muted">{t('exec.chain.notRunYet')}</p>
      )}
      {/* A4: „wartet auf Schulz — Schritt 2: Lieferung".
          Review-Befund Important 1 (Task 10): gefragt wird `blocked_by` und nicht
          `state === 'waiting'`. Die Zustands-Rangfolge (retest -> error -> waiting) verdeckt die
          Sperre an jedem Glied mit eigenem Fehler- oder Retest-Lauf — dort verschwieg die Leiste
          bisher, worauf gewartet wird. `state` bleibt fuer Farbe und Label zustaendig. */}
      {waitingFor && (
        <p className="mt-1 text-text-muted">
          {t('exec.chain.waitingFor', {
            name: waitingFor.tester_name ?? t('exec.chain.unknownTester'),
            sequence: waitingFor.sequence,
            title: pickLang(lang, waitingFor.title_de, waitingFor.title_en),
          })}
        </p>
      )}
      {/* A5: „Die Begruendung ist in der Kettenleiste sichtbar." */}
      {release && (
        <p className="mt-1 text-text-body">
          {t('exec.chain.releasedBy', {
            name: release.released_by_name ?? t('exec.chain.unknownTester'),
            reason: release.reason,
          })}
        </p>
      )}
      {/* A5: der Notausgang — nur an GESPERRTEN Gliedern, nur fuer Berechtigte, nur solange keine
          Freigabe steht. „Gesperrt" heisst auch hier `blocked_by` (Review-Befund Important 1):
          gerade das Glied mit eigenem Fehler UND offenem Vorgaenger braucht den Ausgang, und genau
          dort bot ihn die an `state` gehaengte Bedingung nicht an. */}
      {waitingFor && canRelease && !release && (
        <ReleaseForm
          projectId={projectId}
          planId={planId}
          e2eId={e2eId}
          testCaseId={link.test_case_id}
          onReleased={onReleased}
        />
      )}
      {attachmentsQuery.isLoading && <p className="mt-1 text-text-muted">{t('common.loading')}</p>}
      {/* #89 auch hier: ohne diesen Zweig verschwaenden die Vorschaubilder bei einem Abruffehler
          still — der Tester saehe einen Lauf ohne Anhaenge und wuesste nicht, dass es welche gibt. */}
      {attachmentsQuery.isError && (
        <QueryErrorNotice
          compact
          className="mt-1"
          onRetry={() => void attachmentsQuery.refetch()}
        />
      )}
      {images.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {images.map((src, i) => (
            <button
              key={`${link.test_case_id}-${i}`}
              type="button"
              onClick={() => setZoom(i)}
              className="block"
            >
              {/* alt statt leer: sonst hat die Schaltflaeche keinen zugaenglichen Namen. */}
              <img
                src={src}
                alt={imageLabel(i)}
                className="h-12 w-16 rounded-btn border border-[var(--border)] object-cover"
              />
            </button>
          ))}
        </div>
      )}
      {zoom !== null && (
        <ImageLightbox
          images={images}
          startIndex={zoom}
          label={imageLabel(zoom)}
          onClose={closeZoom}
        />
      )}
    </div>
  );
}

// A5: Begruendung ist PFLICHT. Sie faellt hier zuerst (damit der Nutzer den Grund sofort sieht)
// und ein zweites Mal serverseitig (`E2eReleaseCreateSchema`, `.trim().min(1)`) — die Oberflaeche
// ist die Bequemlichkeit, der Server der Riegel. Gesendet wird der GETRIMMTE Wert, also genau der,
// der geprueft wurde (Lehre aus Task 5).
function ReleaseForm({
  projectId,
  planId,
  e2eId,
  testCaseId,
  onReleased,
}: {
  projectId: string;
  planId: string;
  e2eId: string;
  testCaseId: string;
  onReleased: (release: ChainRelease) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fieldId = `chain-release-reason-${testCaseId}`;

  const invalidate = () => {
    // Die Freigabe aendert, ob das Glied ausfuehrbar ist — alle Sichten sind danach veraltet.
    // Praefix statt Einzelschluessel: die Gruppenansicht liest unter
    // ['e2e-chain', projectId, planId, 'batch', ids] und ist mit e2eId allein nicht getroffen.
    void queryClient.invalidateQueries({ queryKey: ['e2e-chain', projectId, planId] });
    void queryClient.invalidateQueries({ queryKey: ['playlist', projectId, planId] });
    // #195: Die Weitergabe-Übersicht liest dieselbe Ausführbarkeit — sonst bleibt `mine` veraltet.
    void queryClient.invalidateQueries({ queryKey: ['handover', projectId, planId] });
  };

  const mutation = useMutation({
    mutationFn: (trimmed: string) =>
      runsApi.createRelease(projectId, planId, e2eId, {
        test_case_id: testCaseId,
        reason: trimmed,
      }),
    onSuccess: (created) => {
      setOpen(false);
      setError(null);
      onReleased({
        reason: created.reason,
        released_by_name: created.released_by_name,
        released_at: created.released_at,
      });
      invalidate();
    },
    onError: (err) => {
      // 409 `e2e_release_exists` ist KEIN Fehler, sondern eine Auskunft: jemand anders war
      // schneller. Die bestehende Freigabe kommt im Rumpf mit und wird angezeigt — sonst saehe der
      // Testmanager „Es ist ein Fehler aufgetreten" und wuesste nicht, dass sein Glied laengst
      // frei ist (runs.ts, Kommentar am Endpunkt).
      const existing = parseReleaseConflict(err);
      if (existing) {
        setOpen(false);
        setError(null);
        onReleased(existing);
        invalidate();
        return;
      }
      setError(mapApiError(t, err));
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 rounded-btn border border-[var(--border)] px-2 py-1 text-[11px] text-text-body hover:border-brand-accent"
      >
        {t('exec.chain.releaseAction')}
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-1">
      <label htmlFor={fieldId} className="text-[11px] text-text-muted">
        {t('exec.chain.releaseReason')}
      </label>
      <textarea
        id={fieldId}
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('exec.chain.releaseReasonPlaceholder')}
        className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body"
      />
      {error && (
        <p role="alert" className="text-[11px] text-status-fail">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            const trimmed = reason.trim();
            if (!trimmed) {
              setError(t('exec.chain.releaseReasonRequired'));
              return;
            }
            setError(null);
            mutation.mutate(trimmed);
          }}
          className="rounded-btn bg-brand-accent px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
        >
          {t('exec.chain.releaseSubmit')}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="rounded-btn border border-[var(--border)] px-2 py-1 text-[11px] text-text-body"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
